import { axios } from "utils";

import { PUBLIC_USER_URL as endpoint } from "consts";

import {
  AppTypeString,
  CacheInvalidation,
  ChangePasswordParams,
  CheckPasswordParams,
  ConfirmNewEmailParams,
  ConfirmUserRequestBody,
  PlatformContract,
  PlatformTermsConsent,
  SigninUserParams,
  SignupUserParams,
  User,
  UserSettings,
} from "models";

export const signinUser = async (data: SigninUserParams): Promise<{ success: boolean; token: string }> =>
  axios.post(`${endpoint}/signin`, { data, signal: data.signal });

export const checkPassword = async (data: CheckPasswordParams): Promise<{ token: string }> =>
  axios.post(`${endpoint}/check-password`, { data, signal: data.signal });

export const signupUser = async (data: SignupUserParams) => axios.post(`${endpoint}/signup`, { data, signal: data.signal });

export const confirmUser = async ({
  data,
  signal,
}: {
  data: ConfirmUserRequestBody;
  signal: AbortSignal;
}): Promise<{
  token: string;
}> => axios.post(`${endpoint}/confirm`, { data, signal });

export const resetPassword = async (data: { signal: AbortSignal }): Promise<{ success: boolean }> =>
  axios.post(`${endpoint}/password`, { data, signal: data.signal });

export const changePassword = async (data: ChangePasswordParams): Promise<{ success: boolean; token: string }> =>
  axios.post(`${endpoint}/password-reset`, { data, signal: data.signal });

export const verifyUser = async (data: { appType: AppTypeString; signal: AbortSignal }): Promise<{ verified: true; token: string; updated: boolean }> =>
  axios.get(`${endpoint}/verify`, { data, signal: data.signal, timeout: Infinity });

export const getUserSettings = async (data: { signal: AbortSignal }): Promise<UserSettings> => axios.get(`${endpoint}/settings`, { data, signal: data.signal });

export const getUserCacheInvalidation = async (): Promise<CacheInvalidation> => axios.get(`${endpoint}/cache-invalidation`);

export const getUserData = async (data: { appType: AppTypeString; signal: AbortSignal }): Promise<User> =>
  axios.get(`${endpoint}/user-data`, { data, signal: data.signal, timeout: Infinity });

export const getUserNotificationSettings = async (data: { token: string; signal: AbortSignal }) =>
  axios.get(`${endpoint}/settings/notifications/${data.token}`, { data, signal: data.signal });

export const updateNotificationSettings = async (data: { signal: AbortSignal }) =>
  axios.put(`${endpoint}/settings/notifications`, { data, signal: data.signal });

export const updateProfile = async (data: { signal: AbortSignal }) => axios.put(`${endpoint}`, { data, signal: data.signal });

export const updateBio = async (data: { signal: AbortSignal }) => axios.put(`${endpoint}/bio`, { data, signal: data.signal });

export const updateUserSetting = async (data: { item: string; value: unknown; signal?: AbortSignal }) =>
  axios.put(`${endpoint}/setting`, { data, signal: data.signal });

export const updateEmail = async (data: { signal: AbortSignal }) => axios.put(`${endpoint}/email`, { data, signal: data.signal });

export const updatePassword = async (data: { signal: AbortSignal }) => axios.put(`${endpoint}/password`, { data, signal: data.signal });

export const deleteAccount = async (data: { password: string }, signal: AbortSignal) => axios.delete(`${endpoint}/account`, { data, signal });

export const getNotificationList = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}/notifications`, { data, signal: data.signal });

export const updatePush = async (data: { signal: AbortSignal }) => axios.put(`${endpoint}/notifications/push`, { data, signal: data.signal });

export const updateCoachMark = async (data: { signal: AbortSignal }) => axios.put(`${endpoint}/coach-mark`, { data, signal: data.signal });

export const getUserSwimlanes = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}/swimlanes`, { data, signal: data.signal });

export const getUserOnboardingTasks = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}/onboarding-tasks`, { data, signal: data.signal });

export const getMinVersion = async (data: { signal: AbortSignal }): Promise<{ minAppVersion: number }> =>
  axios.get(`${endpoint}/min-version`, { data, signal: data.signal });

export const confirmNewEmail = async (data: ConfirmNewEmailParams): Promise<{ success: boolean }> =>
  axios.put(`${endpoint}/confirm-new-email`, { data, signal: data.signal });

export const logMixpanelId = async (data: { id: string; signal: AbortSignal }) => axios.put(`${endpoint}/mixpanel`, { data, signal: data.signal });

export const getWebinarLink = async (data?: { signal?: AbortSignal }): Promise<{ webinarLink: string }> =>
  axios.get(`${endpoint}/webinar-link`, { data, signal: data?.signal });

export const postEffortScoreFeedback = async (data: { signal: AbortSignal }) => axios.post(`${endpoint}/effort-score-feedback`, { data, signal: data.signal });

// platform terms & conditions

export const addPlatformTermsConsent = async (data: { accepted?: boolean }): Promise<PlatformTermsConsent> =>
  axios.post(`${endpoint}/terms/consents`, { data });

export const getLatestPlatformContract = async (): Promise<PlatformContract> => axios.get(`${endpoint}/terms`);
